import {
  UseMutationOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { useAPI } from 'hooks/api/useAPI';

import APIError from 'types/api_error';
import { RtspConfig } from 'types/rtsp_config';
import { Stream, StreamSource, StreamState, StreamType } from 'types/stream';

import {
  updateQueriesData,
  UpdateQueriesDataContext,
} from 'services/update_queries_data';

type UseUpdateStreamParams = {
  id: string;
  name?: string;
  source?: StreamSource;
  stream_type?: StreamType;
  gateway_id?: string | null;
  uri?: string;
  insecure_disable_tls_verification?: boolean;
  status?: StreamState;
  camera_id?: string | null;
  deployment_id?: string | null;
  node?: string | null;
  snapshot_file_id?: string | null;
  rtsp_config?: RtspConfig | null;
};

export function useUpdateStream({
  onMutate,
  onError,
  onSettled,
  ...options
}: UseMutationOptions<
  Stream,
  APIError,
  UseUpdateStreamParams,
  UpdateQueriesDataContext<Stream>
> = {}) {
  const queryClient = useQueryClient();
  const { applicationID, client } = useAPI();

  return useMutation(
    async ({ id, ...params }) => {
      const { data } = await client.put(
        `/apps/${applicationID}/streams/${id}`,
        { ...params }
      );

      return data;
    },
    {
      ...options,
      onMutate: async ({ id, ...params }) => {
        await queryClient.cancelQueries(['streams']);
        await queryClient.cancelQueries(['stream']);

        onMutate?.({ id, ...params });

        return updateQueriesData({
          queryClient,
          listQueryKey: ['streams'],
          singleQueryKey: ['stream'],
          ids: [id],
          updateData: (stream) => ({ ...stream, ...params }),
        });
      },
      onError: (error, updatedStream, context) => {
        onError?.(error, updatedStream, context);

        context?.previousLists?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
        context?.previousSingles?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
      },
      onSettled: (...args) => {
        onSettled?.(...args);
        queryClient.invalidateQueries(['streams']);
        queryClient.invalidateQueries(['stream']);
        queryClient.invalidateQueries(['video-sources']);
      },
    }
  );
}
